import { SvgIconComponent } from '@mui/icons-material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import BusinessIcon from '@mui/icons-material/Business';
import ApiIcon from '@mui/icons-material/Api';
import EmailIcon from '@mui/icons-material/Email';
import TimelineIcon from '@mui/icons-material/Timeline';

/**
 * Navigation menu item type definition
 */
export interface MenuItem {
  id: string;
  label: string;
  icon: SvgIconComponent;
  path?: string;
  action?: 'logout'; // Special actions that don't navigate
  children?: MenuItem[];
}

/**
 * Navigation configuration structure
 * Separates primary navigation (main features) from secondary (settings, help, etc.)
 */
export interface NavigationConfig {
  primary: MenuItem[];
  secondary: MenuItem[];
}

/**
 * Main navigation configuration
 * This is the single source of truth for all navigation items in the application
 */
export const navigationConfig: NavigationConfig = {
  /**
   * Primary navigation items
   * These appear at the top of the sidebar and represent main application features
   */
  primary: [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: DashboardIcon,
      path: '/dashboard',
    },
    {
      id: 'accounts',
      label: 'Accounts',
      icon: BusinessIcon,
      path: '/accounts',
    },
    {
      id: 'api-clients',
      label: 'API Clients',
      icon: ApiIcon,
      path: '/api-clients',
    },
    {
      id: 'messaging',
      label: 'Messaging',
      icon: EmailIcon,
      path: '/messaging',
    },
    {
      id: 'activity',
      label: 'Activity',
      icon: TimelineIcon,
      path: '/activity',
    },
  ],

  /**
   * Secondary navigation items
   * These appear at the bottom of the sidebar and represent secondary features
   */
  secondary: [
    {
      id: 'settings',
      label: 'Settings',
      icon: SettingsIcon,
      path: '/settings',
    },
    {
      id: 'logout',
      label: 'Logout',
      icon: LogoutIcon,
      action: 'logout', // Special action, no path
    },
  ],
};

/**
 * Helper function to check if a menu item matches the current path
 * Used for active route highlighting
 *
 * @param itemPath - The path defined in the menu item
 * @param currentPath - The current location pathname
 * @returns true if the item should be marked as active
 */
export function isMenuItemActive(itemPath: string | undefined, currentPath: string): boolean {
  if (!itemPath) return false;

  // Exact match
  if (itemPath === currentPath) return true;

  // Path prefix match (for nested routes)
  // Example: /dashboard/users should highlight the /dashboard item
  return currentPath.startsWith(itemPath + '/');
}
